import { Component, Element, Event, EventEmitter, forceUpdate, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";

import { supportsPassiveEvents } from "../../../utils/supportsPassiveEvents";
import { debounce } from "../../../utils/debounce";
import { getUrlParameter } from "../../../utils/getUrlParameter";
import { findTab, findTextInTab } from "../utils/findTab";
import { scrollToTarget } from "../../../utils/scrollToTarget";
import { i18nProvider } from "@trumpf/ux.i18n";

export type Direction = "prev" | "next" | "click-on-tab" | false | null;

interface InternalSetActiveTabOptions {
    id: string;
    scroll: boolean;
    direction: Direction;
    /** this should be true when the component is setup */
    initialCall: boolean;
}

@Component({
    tag: "ux-tab-container",
    styleUrl: "tab-container.scss",
    shadow: false
})
export class TabContainer {
    private children: Array<HTMLUxTabElement>;
    private tabIds: Array<string>;
    private isFormStepsContainer = false;

    @Element() domNode!: HTMLUxTabContainerElement;

    /**
     * Prop which holds the active tab id
     */
    @Prop({
        reflect: true,
        mutable: true
    })
    activeTabId = "";

    /**
     * When this is true, the tab navigation will not be displayed
     */
    @Prop() readonly noNavigation: boolean = false;

    /**
     * Classes to be passed down to the tab-header div, to facilitate integration
     */
    @Prop() readonly tabHeaderClasses: string = "";

    /**
     * Pass in the desired justify-content flexbox value for the alignment of the tab buttons
     */
    @Prop() readonly alignTabButtons: string = "center";

    /**
     * If set, the page will scroll so that the tab-header is at the top if the page
     * can be set either to true, and the target element will be the tab header, or a css selector
     * can be used to define the scroll target element
     */
    @Prop() readonly scrollWhenActivated: boolean | string = false;

    /**
     * If set, will fix the tab header to the top of the screen upon scrolling past it
     */
    @Prop() readonly fixToTop: boolean = false;

    /**
     * Normally, if a "q" parameter is present in the url, the component will try to look for this q
     * parameter value in the content of each tab, and if a match is found, it will activate that tab.
     * With this property set, the component will not perform any checking on the "q" parameter.
     */
    @Prop() readonly ignoreSearchParameter: boolean = false;

    /**
     * If set, the tabs will act like an anchor navigation
     */
    @Prop() readonly anchorNavigation: boolean = false;

    /**
     * Style variant of the tab button
     */
    @Prop() readonly variant: "default" | "folder" = "default";

    /** When active, the scrollable arrow navigation is enabled */
    @State() arrowNavigationEnabled = false;

    /** Holds the offset position of the inner div of the scrollable tab-header */
    @State() offset = 0;

    /** Makes the tab header aware that one of the nav arrows should have disabled styles */
    @State() scrollEnd: "right" | "left";

    /** Holds the value of the width of the tab list element, used for styling */
    @State() tabListWidth: number;

    /** Flag that indicates if the tab header is in the fixed position */
    @State() tabHeaderFixed = false;

    /** Holds the value of the tab header width */
    @State() tabHeaderWidth: number;

    /** Holds the value of the tab header left offset */
    @State() tabHeaderLeft: number;

    /** The max offset for the scroll of the tab list */
    @State() maxOffset: number;

    /** Takes care of passing the correct classes to the navigation arrows for the shadows */
    @State() tabOverlapping: { left: boolean; right: boolean } = {
        left: false,
        right: true
    };

    private firstVisibleTabIndex: number;
    private maxTabIndex: number;
    private tabHeader: HTMLDivElement;
    private tabHeaderHeight = 0;
    private tabHeaderOffsetTop: number;
    private tabList: HTMLDivElement;
    private tabBody: HTMLDivElement;
    private tabButtons: { [id: string]: HTMLUxTabButtonElement } = {};

    /**
     * Unfortunately Safari includes margin in the offsetLeft property of the elements.
     * This property detects Safari browsers and fixes this issue by subtracting the margin
     * for a correct offset calculation
     */
    private isBrowserSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    @Listen("uxTabNameChange", { target: "window" })
    updateTabButtonName() {
        // updates button navigation state on tab button name change
        this.onResize();
    }

    @Watch("arrowNavigationEnabled")
    updateNavigation() {
        setTimeout(() => {
            this.manageArrowNavigation(null);
        }, 400);
    }

    /**
     * Event when the active tab was set
     */
    @Event({
        eventName: "uxActiveTabSet",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    activeTabEvent!: EventEmitter<string>;

    /**
     * Id of the tab which will be active
     */
    @Method()
    async setActiveTab(id: string, scroll: boolean = false, direction: Direction = null) {
        this.internalSetActiveTab({ id, scroll, direction, initialCall: false });
    }

    private internalSetActiveTab({ id, scroll, direction, initialCall }: InternalSetActiveTabOptions) {
        if (!(direction === "click-on-tab" && this.anchorNavigation) && (initialCall || this.activeTabId !== id)) {
            this.activeTabId = id;
            this.activeTabEvent.emit(id);
        }
        this.handleArrowNavigation(direction);
        if (scroll) {
            this.scrollToElement(id);
        }
    }

    /**
     * PURPOSE: gets the total width of the lab list containing the tab buttons
     */
    private getTabListWidth() {
        if (this.tabList === undefined) {
            return;
        }
        if (this.tabList === null) {
            // @ts-expect-error Check for typing
            this.tabList = document.querySelector(".ux-tab-container__tab-list");
        }
        let tabListWidth = 0;
        const tabElements = this.tabList.querySelectorAll("ux-tab-button");
        for (let i = 0; i < this.maxTabIndex + 1; i++) {
            /**
             * we add 5 to each element, value corresponding the te margin left
             * of the element, not included in the clientWidth
             */
            tabListWidth = tabListWidth + tabElements[i].clientWidth + 5;
        }
        this.tabListWidth = tabListWidth - 5;
    }

    /**
     * CALLED: on component load and on resize events
     * PURPOSE: calculates what the maximum horizontal offset of the
     *          tab list should be
     */
    private updateMaxOffset = () => {
        if (this.tabHeader === undefined) {
            return;
        }
        this.getTabListWidth();
        const parentWidth = this.tabHeader.clientWidth;
        // we add 60 to the max offset since the tab list has 30px of margin on each side
        this.maxOffset = this.tabListWidth - (this.tabHeaderFixed ? this.tabBody.getBoundingClientRect().width : parentWidth) + 60;
    };

    /**
     * CALLED: whenever there needs to be an horizontal offset chenge of the tab list
     *         trriggered by the arrow navigation or by a resize event.
     *         When there is a click on a tab button, or a click on an external link
     *         that activates a tag, the function in charge of updating the offset is
     *         updateSelectionOffset
     */
    private updateOffset = (): void => {
        if (this.tabList === null) {
            return;
        }
        let offset = this.tabList.querySelectorAll("ux-tab-button")[this.firstVisibleTabIndex].offsetLeft;
        if (this.isBrowserSafari) {
            offset = offset - 30;
        }
        this.offset = offset < 5 ? 0 : offset < this.maxOffset ? offset : this.maxOffset;
        this.lastAction = "arrow-navigation-click";
        this.updateScrollEnd();
    };

    /**
     * CALLED: every time the horizontal offset of the tab list containing the button is
     *         calculated.
     * PURPOSE: it assigns the scroll end of the tab list, either "left" or "right" if the
     *          tab list has reached its maxumum scroll, or null of not.
     */
    private updateScrollEnd = () => {
        if (this.offset === this.maxOffset || this.firstVisibleTabIndex === this.maxTabIndex) {
            this.scrollEnd = "right";
        } else if (this.offset === 0) {
            this.scrollEnd = "left";
        } else {
            // @ts-expect-error Check for typing
            this.scrollEnd = null;
        }
        this.updateTabOverlapping();
    };

    /**
     * PURPOSE: calls the appropriate offset calculation function depending on the
     *          direction argument.
     */
    private handleArrowNavigation = (direction: Direction) => {
        if (this.tabList === undefined) {
            return;
        }
        if (direction === "next") {
            this.lastAction = "arrow-navigation-click";
            if (this.maxOffset === this.offset) {
                return;
            }
            this.firstVisibleTabIndex =
                this.firstVisibleTabIndex < this.maxTabIndex ? this.firstVisibleTabIndex + 1 : this.firstVisibleTabIndex;
            this.updateOffset();
        } else if (direction === "prev") {
            this.lastAction = "arrow-navigation-click";
            if (this.offset === 0) {
                return;
            }
            this.firstVisibleTabIndex = this.firstVisibleTabIndex > 0 ? this.firstVisibleTabIndex - 1 : this.firstVisibleTabIndex;
            this.updateOffset();
        } else {
            const currentTabActive = this.children.find((tab) => tab.tabId === this.activeTabId);
            // @ts-expect-error Check for typing
            this.firstVisibleTabIndex = this.children.indexOf(currentTabActive) || 0;
            if (direction === "click-on-tab" || !direction) {
                this.updateSelectionOffset();
            } else {
                this.updateOffset();
            }
            this.updateFirstVisibleTabIndex();
        }
    };

    /**
     * CALLED: when the scroll direction was neither "prev" or "next"
     * PURPOSE: gets the correct first visible element skipping possible
     *          cropped elements, in order to perform follwoing offset calculation
     *          taking as a reference the correct element.
     */
    private updateFirstVisibleTabIndex = () => {
        setTimeout(() => {
            if (this.tabList === null) {
                return;
            }
            const elements = this.tabList.querySelectorAll("ux-tab-button");
            const parentOffset = (this.tabList.parentNode as HTMLElement).getBoundingClientRect().left;
            for (let i = 0; i < elements.length; i++) {
                const elementOffset = elements[i].getBoundingClientRect().left;
                if (elementOffset > parentOffset) {
                    this.firstVisibleTabIndex = i;
                    break;
                }
            }
        }, 400);
    };

    /** refs of the arrow navigation elements */
    private arrowRight: HTMLElement;
    private arrowLeft: HTMLElement;

    /** object holding the DOMRects of the arrow navigation elements */
    private arrowsRects: { rightArrow: DOMRect; leftArrow: DOMRect } = {
        // @ts-expect-error Check for typing
        rightArrow: null,
        // @ts-expect-error Check for typing
        leftArrow: null
    };

    /**
     * CALLED: on resize and when there is a function that reqires the rects and
     *         these have not been calculated yet
     * PURPOSE: gets the DOMRects of the arrow navigation elements
     */
    private getArrowsRects = () => {
        const arrowRightRects = this.arrowRight?.getBoundingClientRect();
        const arrowLeftRects = this.arrowLeft?.getBoundingClientRect();
        this.arrowsRects = {
            rightArrow: arrowRightRects,
            leftArrow: arrowLeftRects
        };
    };

    /**
     * CALLED: when the arrow navigation is active and there is a click on a tab element
     * PURPOSE: if the clicked tab button is partially covered by the arrow navigation,
     *          it will slide the tab list so that the selected button is fully visible
     */
    private updateSelectionOffset = () => {
        if (!this.arrowNavigationEnabled) {
            return;
        }
        const activeElement = this.tabButtons[this.activeTabId];
        if (activeElement === undefined) {
            return;
        }
        this.lastAction = "tab-button-click";
        const isLastElement = activeElement.nextSibling === null;
        const isFirstElement = activeElement.previousSibling === null;
        this.getArrowsRects();
        const activeElementRects = activeElement.getBoundingClientRect();
        if (activeElementRects.right > this.arrowsRects.rightArrow?.left) {
            this.offset = isLastElement
                ? this.maxOffset
                : this.offset + Math.floor(activeElementRects.right - this.arrowsRects.rightArrow.left);
        } else if (activeElementRects.left < this.arrowsRects.leftArrow?.right) {
            this.offset = isFirstElement
                ? 0
                : this.offset + Math.floor(activeElementRects.left - this.arrowsRects.leftArrow?.right);
        }
        this.updateScrollEnd();
    };

    /** traks what action was performed last, helps define tab overlapping styles */
    @State() lastAction: null | "tab-button-click" | "arrow-navigation-click" = null;
    /**
     * CALLLED: when the arrow navigation is active & there is a click on a
     *          navigation arrow or an tab change
     * PURPOSE: manages the overlapping styles of the navigation arrows
     */
    private updateTabOverlapping = () => {
        if ((this.lastAction !== "tab-button-click" && this.scrollEnd !== "right") || this.scrollEnd === "left") {
            this.tabOverlapping = { right: true, left: false };
            return;
        }
        if (this.lastAction !== "tab-button-click") {
            if (this.scrollEnd === "right") {
                this.tabOverlapping = { right: false, left: true };
            }
            return;
        }
        if (this.arrowsRects.rightArrow === undefined) {
            this.getArrowsRects();
        }
        if (this.arrowsRects.rightArrow === undefined) {
            return;
        }
        const activeElement = this.tabButtons[this.activeTabId];
        if (activeElement === undefined) {
            return;
        }
        const activeElementRects = activeElement.getBoundingClientRect();
        /**
         * we use 35 in this calculation because it is the distance (rounded down) between
         * the tab button texts. if the distance is greater than 35 it means the text
         * of the following/next non-active tab is visible and we have to activate the
         * overlap, and we are sure that the overlapping pseudo element will not cover
         * the blue background of the active tab causing an unpleasant and buggy feel.
         */
        const tabButtonTextDistance = 35;
        if (activeElementRects.left - this.arrowsRects.leftArrow.right < tabButtonTextDistance) {
            this.tabOverlapping = { ...this.tabOverlapping, left: false };
        } else {
            this.tabOverlapping = { ...this.tabOverlapping, left: true };
        }
        if (this.arrowsRects.rightArrow.left - activeElementRects.right < tabButtonTextDistance) {
            this.tabOverlapping = { ...this.tabOverlapping, right: false };
        } else {
            this.tabOverlapping = { ...this.tabOverlapping, right: true };
        }
    };

    /**
     * called on resize and on reload, updates the dimension of the tab header
     * element for a correct display of the header fixed state
     */
    private setElementDimensions = () => {
        if (!this.fixToTop || this.noNavigation) {
            return;
        }
        this.tabHeaderOffsetTop =
            (this.tabHeaderFixed ? this.tabBody.getBoundingClientRect().top : this.tabHeader.getBoundingClientRect().top) -
            document.body.getBoundingClientRect().top;
        this.tabHeaderWidth = this.tabBody.getBoundingClientRect().width;
        this.tabHeaderLeft = this.tabBody.getBoundingClientRect().right / 2 + this.tabBody.getBoundingClientRect().left / 2;
    };

    @Listen("uxSliderInitialised", { target: "window" })
    setDimensionsOnSliderInit() {
        this.onResize();
    }

    /**
     * function that activates or deactivates the arrow navigation in the tab
     * header based on the widths of the tab header container and the tab linst
     * containig the tab buttons
     */
    private manageArrowNavigation = (direction: Direction = null) => {
        if (this.noNavigation) {
            return;
        }
        if (!this.tabHeader || !this.tabList) {
            return;
        }
        const tabHeaderWidth = this.tabHeaderFixed ? this.tabHeaderWidth : this.tabHeader.getBoundingClientRect().width;
        if (this.tabListWidth > tabHeaderWidth) {
            this.handleArrowNavigation(direction);
            if (!this.arrowNavigationEnabled) {
                this.arrowNavigationEnabled = true;
            }
        } else {
            if (this.arrowNavigationEnabled) {
                this.arrowNavigationEnabled = false;
            }
        }
    };

    /**
     * CALLED: whenever a click event occours. Then a check is performed to detect
     *         the presence of the "data-ux-tab-target" attribute on the clicked element.
     * PURPOSE: handles the click click on an element that contains the data-attribute
     *          data-ux-tab-target. The corresponding tab is activated and the a the
     *          scrollToElement function is called.
     */
    private handleLinkClick = (e: Event) => {
        const targetElement = e.target as Element;
        const tabTargetAttr = "data-ux-tab-target";
        const targetLink = targetElement.closest("a");

        let selectedTabId =
            targetElement.getAttribute(tabTargetAttr) ||
            targetLink?.getAttribute(tabTargetAttr) ||
            targetElement.closest("button")?.getAttribute(tabTargetAttr);

        const hasSelectedTabId = () => !!selectedTabId;

        // Handle clicks on links with #tab anchor
        if (!hasSelectedTabId() && targetLink?.href.includes("#tab")) {
            let anchorTab = "";
            if (targetLink.href.startsWith("#")) {
                anchorTab = targetLink.href.slice(1);
            } else if (targetLink.href.startsWith("http")) {
                const linkUrl = new URL(targetLink.href);
                if (linkUrl.pathname === window.location.pathname) {
                    anchorTab = linkUrl.hash.slice(1);
                }
            } else if (targetLink.href.startsWith("/")) {
                const parts = targetLink.href.split("#");
                if (parts[0] === window.location.pathname) {
                    anchorTab = parts[1];
                }
            }
            if (this.tabIds.includes(anchorTab)) {
                selectedTabId = anchorTab;
            }
        }

        if (!hasSelectedTabId()) {
            return;
        }
        e.preventDefault();
        /** scroll to tab element and activate corresponding tab */
        // @ts-expect-error Check for typing
        if (this.tabIds.includes(selectedTabId)) {
            // @ts-expect-error Check for typing
            this.scrollToElement(selectedTabId, true);
        }
        // @ts-expect-error Check for typing
        if (this.tabIds.includes(selectedTabId)) {
            // @ts-expect-error Check for typing
            this.setActiveTab(selectedTabId, false, false);
        }
    };

    /**
     * PURPOSE: scrolls to the element: either the tab header or an element defined by
     *          setting the scroll-when-activated prop to a css selector, the function
     *          will try to look for that element and scroll to it.
     *          If the tab header already in the fixed mode, it will scroll to to the
     *          top of the tab body.
     */
    private scrollToElement = (id: string, linkActivationScroll: boolean = false): void => {
        if (this.anchorNavigation) {
            this.anchorNavigationScroll(id);
            return;
        }
        if ((this.scrollWhenActivated === false && !linkActivationScroll) || this.tabHeader === undefined) {
            return;
        }
        let scrollTarget = this.tabHeader;
        if (typeof this.scrollWhenActivated === "string" && this.scrollWhenActivated.length > 0) {
            scrollTarget = document.querySelector(`${this.scrollWhenActivated}`) || scrollTarget;
        }
        if (this.tabHeaderFixed) {
            scrollTarget = this.tabBody;
        }
        scrollToTarget({
            target: scrollTarget,
            additionalOffsetTop: 0
        });
    };

    /**
     * CALLED: on component load.
     * PURPOSE: activates a specific tab is a tab parameter with a tab id is present
     *          in the search.
     */
    private activateTabWithSearchParameter = (): void => {
        const directActivationParameter = getUrlParameter("active-tab");
        if (typeof directActivationParameter === "string") {
            const tabToActivate = this.children.find((tab) => tab.tabId === directActivationParameter);
            if (tabToActivate) {
                this.setActiveTab(directActivationParameter, true, false);
                return;
            }
        }
        const directActivationHash = location.hash;
        if (directActivationHash) {
            const hashToId = directActivationHash.replace("#", "");
            if (this.tabIds.includes(hashToId)) {
                this.setActiveTab(hashToId, true, false);
                return;
            }
        }
        if (this.ignoreSearchParameter) {
            return;
        }

        const searchTabParameter = getUrlParameter("tab");
        const searchQParameter = getUrlParameter("q");

        let firstTabIdWithSearchParam;

        if (typeof searchTabParameter === "string") {
            firstTabIdWithSearchParam = findTab(this.domNode, searchTabParameter);
        } else if (typeof searchQParameter === "string") {
            firstTabIdWithSearchParam = findTextInTab(this.domNode, searchQParameter);
        } else {
            return;
        }

        // @ts-expect-error Check for typing
        if (this.tabIds.includes(firstTabIdWithSearchParam)) {
            // @ts-expect-error Check for typing
            this.setActiveTab(firstTabIdWithSearchParam, true, false);
        }
    };

    /**
     * CALLED: on scroll event
     * PURPOSE: handles the assignment of the fixed state of the tab header
     */
    private handleFixedState = () => {
        const scroll = document.documentElement.scrollTop;
        const maxHeight = this.tabHeaderOffsetTop + this.domNode.clientHeight;
        if (scroll >= this.tabHeaderOffsetTop && scroll < maxHeight) {
            this.tabHeaderFixed = true;
        } else {
            this.tabHeaderFixed = false;
        }
    };

    /** SCROLL AND RESIZE FUNCTION HANDLERS */

    private onScroll = () => {
        if (this.fixToTop) {
            this.debouncedDimensions();
            this.handleFixedState();
        }
        if (this.anchorNavigation) {
            this.handleAnchorOnScroll();
        }
    };

    private debouncedDimensions = debounce(() => {
        this.setElementDimensions();
    }, 400);

    private onResize = debounce(() => {
        this.updateMaxOffset();
        this.setElementDimensions();
        this.manageArrowNavigation();
        this.getArrowsRects();
    }, 200);

    /** ANCHOR NAVIGATION SPECIFIC FUNCTIONS */

    private anchorNavigationScroll = (tabId: string) => {
        const scrollTarget = this.children.find((child) => child.tabId === tabId);
        if (!scrollTarget) {
            return;
        }
        scrollToTarget({
            target: scrollTarget,
            additionalOffsetTop: this.tabHeaderHeight
        });
    };

    private handleAnchorOnScroll = debounce(() => {
        if (!this.anchorNavigation) {
            return;
        }
        if (this.tabHeaderHeight === undefined) {
            this.getTabHeaderHeight();
        }
        // @ts-expect-error Check for typing
        const tabsRects = [];
        this.children.forEach((child) => {
            const rects = child.getBoundingClientRect();
            tabsRects.push({
                tabId: child.tabId,
                top: rects.top,
                bottom: rects.bottom
            });
        });
        // @ts-expect-error Check for typing
        let tabToActivate = tabsRects.filter((tabRects) => {
            if (tabRects.top <= this.tabHeaderHeight + 5 && tabRects.bottom > this.tabHeaderHeight + 5) {
                return true;
            }
        })[0];
        if (tabToActivate === undefined) {
            const tabBodyRects = this.tabBody.getBoundingClientRect();
            const isBelowTabsEnd = tabBodyRects.top < 0 && tabBodyRects.height < Math.abs(tabBodyRects.top);
            if (isBelowTabsEnd) {
                tabToActivate = this.children[this.children.length - 0.1];
            } else if (!this.tabHeaderFixed) {
                tabToActivate = this.children[0];
            } else {
                return;
            }
        }
        this.setActiveTab(tabToActivate?.tabId, false, null);
    }, 20);

    private getTabHeaderHeight = () => {
        // @ts-expect-error Check for typing
        this.tabHeaderHeight = this.domNode?.querySelector(".ux-tab-container__tab-header")?.getBoundingClientRect().height;
    };

    /** LIFECYCLE METHODS */

    componentWillLoad() {
        this.isFormStepsContainer = !!this.domNode.closest("ux-form-steps");
        this.children = !this.isFormStepsContainer
            ? (Array.from(this.domNode.children) as HTMLUxTabElement[])
            : Array.from(this.domNode.querySelectorAll("ux-tab") as NodeListOf<HTMLUxTabElement>);
        this.tabIds = this.children.map((tab) => tab.tabId);
        this.maxTabIndex = this.children.length - 1;
        if (this.anchorNavigation) {
            this.children.forEach((child) => (child.anchorNavigation = true));
        }
        const currentTabActive = this.children.find((tab) => tab.tabId === this.activeTabId);
        // @ts-expect-error Check for typing
        this.firstVisibleTabIndex = this.children.indexOf(currentTabActive) || 0;
        if (this.activeTabId !== "") {
            this.internalSetActiveTab({ id: this.activeTabId, direction: null, scroll: false, initialCall: true });
        } else {
            this.internalSetActiveTab({ id: this.children[0]?.tabId, direction: null, scroll: false, initialCall: true });
        }
    }

    componentDidLoad() {
        window.addEventListener("click", this.handleLinkClick);
        window.addEventListener("resize", this.onResize);
        this.updateMaxOffset();
        this.activateTabWithSearchParameter();
        this.manageArrowNavigation();
        window.addEventListener(
            "scroll",
            this.onScroll,
            supportsPassiveEvents
                ? {
                      passive: true
                  }
                : false
        );
        if (this.fixToTop) {
            this.setElementDimensions();
        }
        /**
         * check if parent element has an id and if this id is in the url as hash:
         * if yes, scroll to element when the page height is stable.
         */
        if (this.domNode.hasAttribute("id")) {
            const elementId = this.domNode.getAttribute("id");
            const href = location.hash;
            if (href.substring(1) === elementId) {
                let pageHeight1 = 0;
                let pageHeight2 = 0;
                const pageHeightInterval = setInterval(() => {
                    if (pageHeight1 === pageHeight2 && pageHeight2 === window.innerHeight) {
                        clearInterval(pageHeightInterval);
                        scrollToTarget({
                            target: this.domNode,
                            additionalOffsetTop: 0,
                            smooth: true
                        });
                    } else {
                        pageHeight1 = window.innerHeight;
                        pageHeight2 = pageHeight1;
                    }
                }, 50);
            }
        }
    }

    disconnectedCallback() {
        window.removeEventListener("click", this.handleLinkClick);
        window.removeEventListener("resize", this.onResize);
        window.removeEventListener("scroll", this.onScroll);
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    private handleKeyDownButton(event: KeyboardEvent, tabId: string) {
        if (event.key === "Enter") {
            if (tabId) {
                this.setActiveTab(tabId, true, "click-on-tab");
            }
        }
    }

    private handleKeyDownArrow(event: KeyboardEvent, direction: "prev" | "next") {
        if (!direction) {
            return;
        }
        if (event.key === "Enter") {
            if (direction === "prev") {
                this.handleArrowNavigation("prev");
            } else {
                this.handleArrowNavigation("next");
            }
        }
    }

    private i18nSubscriberId!: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    render() {
        const { alignTabButtons, tabListWidth, arrowNavigationEnabled, tabHeaderFixed, tabHeaderWidth, tabHeaderLeft } = this;

        const tabBodyNoGutter = this.isFormStepsContainer;

        const tabHeaderClasses = ["ux-tab-container__tab-header"];
        if (typeof this.tabHeaderClasses === "string" && this.tabHeaderClasses.length > 0) {
            tabHeaderClasses.push(this.tabHeaderClasses);
        }
        if (tabHeaderFixed) {
            tabHeaderClasses.push("ux-tab-container__tab-header--fixed");
        }

        const alignButtonsValues = ["center", "space-around", "space-evenly", "flex-start", "flex-end"];
        const tabButtonsAlignment = {
            justifyContent: alignButtonsValues.includes(alignTabButtons.toLowerCase()) ? alignTabButtons : "center"
        };

        const tabWithArrowsStyles = arrowNavigationEnabled
            ? {
                  transform: `translateX(-${this.offset}px)`,
                  width: `${tabListWidth}px`
              }
            : {};

        const tabListStyles = {
            ...tabButtonsAlignment,
            ...tabWithArrowsStyles
        };

        const tabBodyStyles = tabHeaderFixed
            ? {
                  borderTop: `${this.tabHeader.getBoundingClientRect().height}px solid transparent`
              }
            : {};

        const tabHeaderStyles = tabHeaderFixed
            ? {
                  width: `${tabHeaderWidth}px`,
                  left: `${tabHeaderLeft}px`
              }
            : {};

        const { t } = i18nProvider;
        const prevLabel = t("tab.previous");
        const nextLabel = t("tab.next");

        return (
            <Host
                class={{
                    [`ux-tab-container`]: true,
                    [`ux-tab-container--arrows-active`]: arrowNavigationEnabled,
                    [`ux-tab-container--folder`]: this.variant === "folder"
                }}
            >
                {!this.noNavigation && (
                    <div
                        class={tabHeaderClasses.join(" ")}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.tabHeader = el)}
                        style={{ ...tabHeaderStyles }}
                    >
                        {this.arrowNavigationEnabled && (
                            <span
                                aria-label={prevLabel}
                                role="button"
                                onClick={() => this.handleArrowNavigation("prev")}
                                onKeyDown={(e) => this.handleKeyDownArrow(e as KeyboardEvent, "prev")}
                                tabindex={this.scrollEnd === "left" ? -1 : 0}
                                aria-disabled={this.scrollEnd === "left" ? true : false}
                                // @ts-expect-error Check for typing
                                ref={(el) => (this.arrowLeft = el)}
                                class={{
                                    "ux-tab-container__arrow": true,
                                    "ux-tab-container__arrow--left": true,
                                    "ux-tab-container__arrow--deactivated": this.scrollEnd === "left",
                                    "is-overlapping": this.tabOverlapping.left
                                }}
                            >
                                <ux-icon name="chevron-left" weight="normal"></ux-icon>
                            </span>
                        )}
                        <div
                            class="ux-tab-container__tab-list"
                            role="tablist"
                            // @ts-expect-error Check for typing
                            ref={(el) => (this.tabList = el)}
                            style={{ ...tabListStyles }}
                        >
                            {this.children.map((tab) => (
                                <ux-tab-button
                                    onClick={() => this.setActiveTab(tab.tabId, true, "click-on-tab")}
                                    onKeyDown={(e) => this.handleKeyDownButton(e as KeyboardEvent, tab.tabId)}
                                    // @ts-expect-error Check for typing
                                    ref={(el) => (this.tabButtons[tab.tabId] = el)}
                                    name={tab.name}
                                    tabId={tab.tabId}
                                    activeTab={this.activeTabId}
                                    class={{ [`ux-tab-button--folder`]: this.variant === "folder" }}
                                    innerHTML={tab.querySelector("[slot='label']")?.innerHTML}
                                />
                            ))}
                        </div>
                        {this.arrowNavigationEnabled && (
                            <span
                                aria-label={nextLabel}
                                onClick={() => this.handleArrowNavigation("next")}
                                role="button"
                                onKeyDown={(e) => this.handleKeyDownArrow(e as KeyboardEvent, "next")}
                                tabindex={this.scrollEnd === "right" ? -1 : 0}
                                aria-disabled={this.scrollEnd === "right" ? true : false}
                                // @ts-expect-error Check for typing
                                ref={(el) => (this.arrowRight = el)}
                                class={{
                                    "ux-tab-container__arrow": true,
                                    "ux-tab-container__arrow--right": true,
                                    "ux-tab-container__arrow--deactivated": this.scrollEnd === "right",
                                    "is-overlapping": this.tabOverlapping.right
                                }}
                            >
                                <ux-icon name="chevron-right" weight="normal"></ux-icon>
                            </span>
                        )}
                    </div>
                )}
                <div
                    class={{
                        ["ux-tab-container__tab-body"]: true,
                        ["ux-tab-container__tab-body--no-gutter"]: tabBodyNoGutter
                    }}
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.tabBody = el)}
                    style={{ ...tabBodyStyles }}
                >
                    <slot />
                </div>
            </Host>
        );
    }
}
